import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useTranslations from '@/i18n/useTranslations';
import UserService from '@/services/user.service';

import { BACKGROUND_COLOR, inspirationTopics, SAVE_LIMIT, STROKE_COLOR } from '../constants';

const MAX_IMAGE_BYTES = 950_000;
const DOWNSCALE_FACTOR = 0.85;
const MAX_DOWNSCALE_ATTEMPTS = 8;

function stripPngPrefix(dataUrl) {
  return dataUrl.replace(/^data:image\/png;base64,/, '');
}

// Exact decoded byte length using atob (server likely uses decoded length)
function decodedByteLength(base64) {
  // Remove any accidental whitespace/newlines
  const clean = base64.replace(/\s+/g, '');
  // NOTE: atob returns a binary string where length === bytes
  // For ~1MB this is safe in the browser.
  return atob(clean).length;
}

function createScaledCanvas(sourceCanvas, scale) {
  if (scale >= 1) {
    return sourceCanvas;
  }

  const width = Math.max(1, Math.floor(sourceCanvas.width * scale));
  const height = Math.max(1, Math.floor(sourceCanvas.height * scale));

  const scaledCanvas = document.createElement('canvas');
  scaledCanvas.width = width;
  scaledCanvas.height = height;

  const ctx = scaledCanvas.getContext('2d');
  ctx.drawImage(sourceCanvas, 0, 0, width, height);

  return scaledCanvas;
}

function exportCanvasAsPngUnderLimit(canvas, t) {
  if (!canvas) {
    throw new Error(t('errors.nothing_to_save'));
  }

  let scale = 1;
  let candidateCanvas = canvas;
  let previousWidth = canvas.width;
  let previousHeight = canvas.height;

  for (let attempt = 0; attempt < MAX_DOWNSCALE_ATTEMPTS; attempt += 1) {
    const dataUrl = candidateCanvas.toDataURL('image/png');
    const base64 = stripPngPrefix(dataUrl);
    const exactBytes = decodedByteLength(base64);

    if (exactBytes < MAX_IMAGE_BYTES) {
      return { dataUrl, base64 };
    }

    scale *= DOWNSCALE_FACTOR;
    const nextWidth = Math.max(1, Math.floor(canvas.width * scale));
    const nextHeight = Math.max(1, Math.floor(canvas.height * scale));

    if (nextWidth === previousWidth && nextHeight === previousHeight) {
      break;
    }

    candidateCanvas = createScaledCanvas(canvas, scale);
    previousWidth = nextWidth;
    previousHeight = nextHeight;
  }

  throw new Error(t('errors.image_too_large'));
}

function normaliseIppitsugakiPiece(rawPiece, fallback = {}) {
  const piece = rawPiece ?? {};
  const base = fallback ?? {};
  const inlineImageData = piece.imageData ?? piece.image_data ?? null;

  const url =
    piece.url ??
    piece.imageUrl ??
    piece.image_url ??
    piece.imageDataUrl ??
    piece.image_data_url ??
    piece.image ??
    (inlineImageData
      ? inlineImageData.startsWith('data:image/png;base64,')
        ? inlineImageData
        : `data:image/png;base64,${inlineImageData}`
      : null) ??
    base.url;

  const timestamp =
    piece.timestamp ??
    piece.savedAt ??
    piece.saved_at ??
    piece.createdAt ??
    piece.created_at ??
    base.timestamp ??
    new Date().toISOString();

  const mode = piece.mode ?? piece.gameMode ?? base.mode ?? 'free';
  const inspirationTopic =
    piece.inspirationTopic ??
    piece.inspiration_topic ??
    piece.prompt ??
    base.inspirationTopic ??
    null;
  const wasEyesClosed =
    piece.wasEyesClosed ??
    piece.was_eyes_closed ??
    piece.eyesClosed ??
    base.wasEyesClosed ??
    false;

  const id = piece.id ?? piece._id ?? base.id ?? timestamp ?? Date.now();

  if (!url) {
    return null;
  }

  return {
    id,
    url,
    timestamp,
    mode,
    inspirationTopic,
    wasEyesClosed,
  };
}

export function useIppitsugakiGame({ onUpgradeRequired } = {}) {
  const t = useTranslations('common.newspaper.art_games.ippitsugaki.game');
  const canvasRef = useRef(null);
  const canvasContainerRef = useRef(null);
  const ctxRef = useRef(null);
  const pointerDataRef = useRef({ lastPoint: null, lastTime: null, width: 4 });
  const activePointerIdRef = useRef(null);
  const hasStrokeRef = useRef(false);
  const resizeFrameRef = useRef(null);
  const lastCanvasSizeRef = useRef({ width: 0, height: 0, dpr: 0 });
  const saveToastTimeoutRef = useRef(null);
  // Stores segments drawn while the line is hidden in eyes-closed mode.
  const eyesClosedSegmentsRef = useRef([]);
  const audioContextRef = useRef(null);

  const [isDrawing, setIsDrawing] = useState(false);
  const [hasStroke, setHasStroke] = useState(false);
  const [mode, setMode] = useState('free');
  const [stabilizerEnabled, setStabilizerEnabled] = useState(true);
  const [cursorPosition, setCursorPosition] = useState(null);
  const [savedPieces, setSavedPieces] = useState([]);
  const [saveToast, setSaveToast] = useState(null);
  const [inspirationTopic, setInspirationTopic] = useState(() => inspirationTopics[0] ?? '');
  const [successStreak, setSuccessStreak] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCurrentPieceSaved, setIsCurrentPieceSaved] = useState(false);

  const stabilizerStrength = useMemo(() => (stabilizerEnabled ? 0.75 : 0.1), [stabilizerEnabled]);

  const showToast = useCallback((message) => {
    clearTimeout(saveToastTimeoutRef.current);
    setSaveToast(message);

    if (message) {
      saveToastTimeoutRef.current = setTimeout(() => {
        setSaveToast(null);
      }, 2500);
    }
  }, []);

  const ensureAudioContext = useCallback(() => {
    if (typeof window === 'undefined') return null;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) {
      return null;
    }
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContextClass();
    }
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume().catch(() => {});
    }
    return audioContextRef.current;
  }, []);

  const playCue = useCallback(
    (type) => {
      const ctx = ensureAudioContext();
      if (!ctx) return;

      const now = ctx.currentTime;
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();

      let startFreq = 240;
      let endFreq = 360;
      let duration = 0.35;
      let gainPeak = 0.2;

      if (type === 'start') {
        startFreq = 180;
        endFreq = 310;
        duration = 0.28;
        gainPeak = 0.12;
      } else if (type === 'lift') {
        startFreq = 320;
        endFreq = 180;
        duration = 0.32;
        gainPeak = 0.08;
      } else if (type === 'save') {
        startFreq = 480;
        endFreq = 620;
        duration = 0.5;
        gainPeak = 0.18;
      } else if (type === 'reset') {
        startFreq = 160;
        endFreq = 90;
        duration = 0.45;
        gainPeak = 0.16;
      }

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(startFreq, now);
      oscillator.frequency.linearRampToValueAtTime(endFreq, now + duration);

      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(gainPeak, now + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

      oscillator.connect(gain);
      gain.connect(ctx.destination);

      oscillator.start(now);
      oscillator.stop(now + duration);
    },
    [ensureAudioContext]
  );

  const triggerHaptic = useCallback((pattern) => {
    if (navigator?.vibrate) {
      navigator.vibrate(pattern);
    }
  }, []);

  const resizeCanvas = useCallback(() => {
    if (resizeFrameRef.current) {
      cancelAnimationFrame(resizeFrameRef.current);
    }

    resizeFrameRef.current = requestAnimationFrame(() => {
      const canvas = canvasRef.current;
      const container = canvasContainerRef.current;
      const ctx = ctxRef.current;
      if (!canvas || !container || !ctx) return;

      const rect = container.getBoundingClientRect();
      const width = Math.max(rect.width, 0);
      const height = Math.max(rect.height, 0);
      const dpr = window.devicePixelRatio || 1;
      const { width: lastWidth, height: lastHeight, dpr: lastDpr } = lastCanvasSizeRef.current;

      const widthDelta = Math.abs(width - lastWidth);
      const heightDelta = Math.abs(height - lastHeight);

      if (widthDelta < 0.5 && heightDelta < 0.5 && dpr === lastDpr) {
        return;
      }

      lastCanvasSizeRef.current = { width, height, dpr };

      const displayWidth = Math.max(1, width);
      const displayHeight = Math.max(1, height || displayWidth * 0.75);
      const bufferWidth = Math.max(1, Math.round(displayWidth * dpr));
      const bufferHeight = Math.max(1, Math.round(displayHeight * dpr));

      canvas.width = bufferWidth;
      canvas.height = bufferHeight;
      canvas.style.width = `${displayWidth}px`;
      canvas.style.height = `${displayHeight}px`;

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, bufferWidth, bufferHeight);
      ctx.fillStyle = BACKGROUND_COLOR;
      ctx.fillRect(0, 0, bufferWidth, bufferHeight);

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineWidth = 4;
      ctx.strokeStyle = STROKE_COLOR;
      ctx.shadowColor = 'rgba(43, 91, 100, 0.15)';
      ctx.shadowBlur = 6;
    });
  }, []);

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (!canvas || !ctx) return;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = BACKGROUND_COLOR;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    hasStrokeRef.current = false;
    setHasStroke(false);
    setIsCurrentPieceSaved(false);
    pointerDataRef.current = { lastPoint: null, lastTime: null, width: 4 };
    eyesClosedSegmentsRef.current = [];
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = canvasContainerRef.current;
    if (!canvas || !container) return;
    const ctx = canvas.getContext('2d');
    ctxRef.current = ctx;

    canvas.style.background = BACKGROUND_COLOR;
    canvas.style.touchAction = 'none';

    resizeCanvas();
    const observer = new ResizeObserver(resizeCanvas);
    observer.observe(container);

    return () => {
      observer.disconnect();
      if (resizeFrameRef.current) {
        cancelAnimationFrame(resizeFrameRef.current);
        resizeFrameRef.current = null;
      }
      clearTimeout(saveToastTimeoutRef.current);
    };
  }, [resizeCanvas]);

  const getCanvasCoordinates = useCallback((event) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = rect.width > 0 ? canvas.width / rect.width : 1;
    const scaleY = rect.height > 0 ? canvas.height / rect.height : 1;
    return {
      x: (event.clientX - rect.left) * (scaleX / (window.devicePixelRatio || 1)),
      y: (event.clientY - rect.top) * (scaleY / (window.devicePixelRatio || 1)),
    };
  }, []);

  const handlePointerMove = useCallback(
    (event) => {
      if (!isDrawing) return;
      event.preventDefault();
      const ctx = ctxRef.current;
      if (!ctx) return;

      const now = performance.now();
      const { x, y } = getCanvasCoordinates(event);
      if (mode === 'eyes-closed') {
        setCursorPosition(null);
      } else {
        setCursorPosition({ x, y });
      }

      const lastPoint = pointerDataRef.current.lastPoint;
      const lastTime = pointerDataRef.current.lastTime;
      const lastWidth = pointerDataRef.current.width;

      if (!lastPoint) {
        pointerDataRef.current.lastPoint = { x, y };
        pointerDataRef.current.lastTime = now;
        pointerDataRef.current.width = lastWidth;
        ctx.beginPath();
        ctx.moveTo(x, y);
        if (mode === 'eyes-closed') {
          eyesClosedSegmentsRef.current = [];
        }
        return;
      }

      const dx = x - lastPoint.x;
      const dy = y - lastPoint.y;
      const distance = Math.sqrt(dx * dx + dy * dy) || 0.0001;
      const elapsed = now - lastTime || 0.0001;
      const speed = distance / elapsed;

      const minWidth = 2.2;
      const maxWidth = 7.2;
      const maxSpeed = 1.8;
      const speedClamped = Math.min(speed, maxSpeed);
      const targetWidth = maxWidth - (speedClamped / maxSpeed) * (maxWidth - minWidth);
      const smoothing = stabilizerStrength;
      const nextWidth = lastWidth * smoothing + targetWidth * (1 - smoothing);

      if (mode === 'eyes-closed') {
        eyesClosedSegmentsRef.current.push({
          from: { x: lastPoint.x, y: lastPoint.y },
          to: { x, y },
          width: nextWidth,
        });
      } else {
        ctx.beginPath();
        ctx.moveTo(lastPoint.x, lastPoint.y);
        ctx.lineTo(x, y);
        ctx.lineWidth = nextWidth;
        ctx.strokeStyle = STROKE_COLOR;
        ctx.stroke();
      }

      pointerDataRef.current.lastPoint = { x, y };
      pointerDataRef.current.lastTime = now;
      pointerDataRef.current.width = nextWidth;
    },
    [getCanvasCoordinates, isDrawing, mode, stabilizerStrength]
  );

  // Draws the hidden segments when the player lifts their finger in eyes-closed mode.
  const revealEyesClosedStroke = useCallback(() => {
    const ctx = ctxRef.current;
    const segments = eyesClosedSegmentsRef.current;
    if (!ctx || segments.length === 0) return;

    ctx.save();
    ctx.strokeStyle = STROKE_COLOR;
    segments.forEach((segment) => {
      ctx.beginPath();
      ctx.moveTo(segment.from.x, segment.from.y);
      ctx.lineTo(segment.to.x, segment.to.y);
      ctx.lineWidth = segment.width;
      ctx.stroke();
    });
    ctx.restore();
    eyesClosedSegmentsRef.current = [];
  }, []);

  const releasePointer = useCallback((pointerId) => {
    if (pointerId === null) return;
    try {
      canvasRef.current?.releasePointerCapture(pointerId);
    } catch (error) {
      // ignore release errors when pointer capture is not set
    }
  }, []);

  const concludeStroke = useCallback(() => {
    setIsDrawing(false);
    hasStrokeRef.current = true;
    setHasStroke(true);
    pointerDataRef.current.lastPoint = null;
    pointerDataRef.current.lastTime = null;
    pointerDataRef.current.width = 4;
    setCursorPosition(null);
  }, []);

  const handlePointerUp = useCallback(
    (event) => {
      if (!isDrawing) return;
      event.preventDefault();
      concludeStroke();
      if (mode === 'eyes-closed') {
        revealEyesClosedStroke();
      }
      const pointerId = event.pointerId ?? activePointerIdRef.current;
      releasePointer(pointerId ?? null);
      activePointerIdRef.current = null;
      playCue('lift');
      triggerHaptic(12);
    },
    [concludeStroke, isDrawing, mode, playCue, releasePointer, revealEyesClosedStroke, triggerHaptic]
  );

  const handlePointerDown = useCallback(
    (event) => {
      event.preventDefault();
      const canvas = canvasRef.current;
      if (!canvas) return;

      if (hasStrokeRef.current) {
        clearCanvas();
      }

      ensureAudioContext();
      playCue('start');
      triggerHaptic(8);

      const ctx = ctxRef.current;
      if (!ctx) return;

      const { x, y } = getCanvasCoordinates(event);
      ctx.beginPath();
      ctx.moveTo(x, y);
      pointerDataRef.current = {
        lastPoint: { x, y },
        lastTime: performance.now(),
        width: 4,
      };

      if (mode === 'eyes-closed') {
        eyesClosedSegmentsRef.current = [];
        setCursorPosition(null);
      } else {
        setCursorPosition({ x, y });
      }

      hasStrokeRef.current = false;
      setHasStroke(false);
      setIsDrawing(true);

      activePointerIdRef.current = event.pointerId ?? null;
      if (event.pointerId != null) {
        canvas.setPointerCapture(event.pointerId);
      }
    },
    [clearCanvas, ensureAudioContext, getCanvasCoordinates, mode, playCue, triggerHaptic]
  );

  const handlePointerCancel = useCallback(
    (event) => {
      if (!isDrawing) return;
      event.preventDefault();
      handlePointerUp(event);
    },
    [handlePointerUp, isDrawing]
  );

  // Randomises the inspiration prompt without immediately repeating the previous word.
  const shuffleInspirationTopic = useCallback(() => {
    if (inspirationTopics.length === 0) {
      setInspirationTopic('');
      return;
    }

    setInspirationTopic((current) => {
      if (inspirationTopics.length === 1) {
        return inspirationTopics[0];
      }

      let nextTopic = current;
      while (nextTopic === current) {
        const randomIndex = Math.floor(Math.random() * inspirationTopics.length);
        nextTopic = inspirationTopics[randomIndex];
      }
      return nextTopic;
    });
  }, []);

  // Persists the current drawing to the in-app gallery without triggering a file download.
  const handleSave = useCallback(async () => {
    if (isSaving) return;

    const canvas = canvasRef.current;
    if (!canvas || !hasStrokeRef.current) return;

    let exportResult;

    try {
      exportResult = exportCanvasAsPngUnderLimit(canvas, t);
    } catch (error) {
      console.error('Failed to prepare Ippitsugaki image', error);
      showToast(error?.message ?? t('errors.default_save'));
      return;
    }

    const { dataUrl } = exportResult;
    const savedAt = new Date();
    const timestamp = savedAt.toISOString();
    const payload = {
      imageData: dataUrl,
      mode,
      inspirationTopic: mode === 'inspiration' ? inspirationTopic : null,
      wasEyesClosed: mode === 'eyes-closed',
      timestamp,
      savedAt: timestamp,
    };

    setIsSaving(true);
    try {
      const { response, data } = await UserService.saveIppitsugakiPiece(payload);

      if (!response?.ok) {
        if (data?.code === 'SUARTE_PLUS_REQUIRED' || data?.data?.code === 'SUARTE_PLUS_REQUIRED') {
          onUpgradeRequired?.(data);
          return;
        }
        const message = data?.message ?? t('errors.default_save');
        throw new Error(message);
      }

      const payloadData = data?.data ?? data ?? {};
      const savedPiece = normaliseIppitsugakiPiece(payloadData?.piece ?? data?.piece ?? payloadData, {
        id: savedAt.getTime(),
        url: dataUrl,
        timestamp: payload.timestamp,
        mode: payload.mode,
        inspirationTopic: payload.inspirationTopic,
        wasEyesClosed: payload.wasEyesClosed,
      });

      const hydratedPieces = Array.isArray(payloadData?.pieces) ? payloadData.pieces : null;

      if (hydratedPieces) {
        setSavedPieces(
          hydratedPieces
            .map((item) => normaliseIppitsugakiPiece(item))
            .filter(Boolean)
            .slice(0, SAVE_LIMIT)
        );
      } else if (savedPiece) {
        setSavedPieces((prev) => {
          const next = [savedPiece, ...prev];
          return next.slice(0, SAVE_LIMIT);
        });
      }

      playCue('save');
      triggerHaptic([20, 40, 20]);
      setSuccessStreak((prev) => prev + 1);
      setIsCurrentPieceSaved(true);
      showToast(t('toasts.saved'));
    } catch (error) {
      console.error('Failed to save Ippitsugaki piece', error);
      showToast(error?.message ?? t('errors.default_save'));
    } finally {
      setIsSaving(false);
    }
  }, [inspirationTopic, isSaving, mode, onUpgradeRequired, playCue, showToast, t, triggerHaptic]);

  // Clears the canvas and refreshes mode-specific helpers when the player wants a fresh start.
  const handleReset = useCallback(() => {
    clearCanvas();
    setCursorPosition(null);
    setSuccessStreak(0);
    playCue('reset');
    triggerHaptic([12, 30, 12]);
    if (mode === 'inspiration') {
      shuffleInspirationTopic();
    }
  }, [clearCanvas, mode, playCue, shuffleInspirationTopic, triggerHaptic]);

  const toggleStabilizer = useCallback(() => {
    setStabilizerEnabled((prev) => !prev);
  }, []);

  const deletePiece = useCallback(async (pieceId) => {
    if (!pieceId || isDeleting) return false;

    setIsDeleting(true);
    try {
      const { response, data } = await UserService.deleteIppitsugakiPiece(pieceId);
      if (!response?.ok) {
        throw new Error(data?.message ?? 'Unable to delete this saved piece.');
      }

      setSavedPieces((pieces) => pieces.filter((piece) => piece.id !== pieceId));
      return true;
    } catch (error) {
      console.error('Failed to delete Ippitsugaki piece', error);
      showToast(error?.message ?? 'Unable to delete this saved piece.');
      return false;
    } finally {
      setIsDeleting(false);
    }
  }, [isDeleting, showToast]);

  useEffect(() => {
    const handleKeySave = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        handleSave();
      }
    };
    window.addEventListener('keydown', handleKeySave);
    return () => {
      window.removeEventListener('keydown', handleKeySave);
    };
  }, [handleSave]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    let isMounted = true;

    const loadPieces = async () => {
      try {
        const { response, data } = await UserService.listIppitsugakiPieces();
        if (!response?.ok) {
          throw new Error(data?.message ?? 'Unable to fetch your gallery.');
        }

        const payloadData = data?.data ?? data ?? {};
        const list = Array.isArray(payloadData?.pieces)
          ? payloadData.pieces
          : Array.isArray(payloadData)
            ? payloadData
            : [];

        const parsedPieces = list
          .map((item) => normaliseIppitsugakiPiece(item))
          .filter(Boolean)
          .slice(0, SAVE_LIMIT);

        if (isMounted) {
          setSavedPieces(parsedPieces);
        }
      } catch (error) {
        console.error('Failed to load Ippitsugaki pieces', error);
      }
    };

    loadPieces();

    return () => {
      isMounted = false;
    };
  }, []);

  return {
    canvasRef,
    canvasContainerRef,
    cursorPosition,
    deletePiece,
    mode,
    setMode,
    inspirationTopic,
    isDeleting,
    shuffleInspirationTopic,
    stabilizerEnabled,
    toggleStabilizer,
    savedPieces,
    saveToast,
    successStreak,
    hasStroke,
    isCurrentPieceSaved,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerCancel,
    handleReset,
    handleSave,
  };
}
